(() => {
  'use strict';

  const MBB = {
    selectors: {
      reveal: '[data-mbb-reveal], .mbb-reveal',
      stagger: '[data-mbb-stagger]',
      scrollLink: 'a[href^="#"]:not([href="#"])'
    },

    classes: {
      visible: 'is-visible',
      loaded: 'is-loaded',
      reducedMotion: 'mbb-reduced-motion'
    },

    settings: {
      revealThreshold: 0.15,
      revealRootMargin: '0px 0px -60px 0px',
      staggerDelay: 100
    },

    init() {
      this.handleReducedMotion();
      this.initPageLoad();
      this.initRevealAnimations();
      this.initStaggerAnimations();
      this.initSmoothScroll();
      this.initExternalLinks();
      document.dispatchEvent(new CustomEvent('mbb:ready'));
    },

    handleReducedMotion() {
      if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      document.documentElement.classList.add(this.classes.reducedMotion);
    },

    initPageLoad() {
      window.addEventListener('load', () => {
        document.documentElement.classList.add(this.classes.loaded);
      }, { once: true });
    },

    initRevealAnimations(scope = document) {
      const elements = scope.querySelectorAll(this.selectors.reveal);
      if (!elements.length) return;

      if (!('IntersectionObserver' in window) || this.prefersReducedMotion()) {
        elements.forEach(element => this.showElement(element));
        return;
      }

      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          this.showElement(entry.target);
          observer.unobserve(entry.target);
        });
      }, {
        threshold: this.settings.revealThreshold,
        rootMargin: this.settings.revealRootMargin
      });

      elements.forEach(element => {
        if (element.dataset.mbbRevealReady === 'true') return;
        element.dataset.mbbRevealReady = 'true';
        observer.observe(element);
      });
    },

    initStaggerAnimations(scope = document) {
      const groups = scope.querySelectorAll(this.selectors.stagger);
      if (!groups.length) return;

      groups.forEach(group => {
        const children = Array.from(group.children);
        const customDelay = Number(group.dataset.mbbStaggerDelay);
        const delay = Number.isFinite(customDelay) ? customDelay : this.settings.staggerDelay;

        children.forEach((child, index) => {
          child.classList.add('mbb-reveal');
          child.style.setProperty('--mbb-reveal-delay', `${index * delay}ms`);
        });
      });

      this.initRevealAnimations(scope);
    },

    initSmoothScroll(scope = document) {
      const links = scope.querySelectorAll(this.selectors.scrollLink);
      if (!links.length) return;

      links.forEach(link => {
        if (link.dataset.mbbScrollReady === 'true') return;
        link.dataset.mbbScrollReady = 'true';

        link.addEventListener('click', event => {
          const targetId = link.getAttribute('href');
          if (!targetId || targetId === '#') return;

          let target;

          try {
            target = document.querySelector(targetId);
          } catch (error) {
            return;
          }

          if (!target) return;

          event.preventDefault();

          const headerOffset = this.getHeaderOffset();
          const targetPosition = target.getBoundingClientRect().top + window.scrollY - headerOffset;

          window.scrollTo({
            top: targetPosition,
            behavior: this.prefersReducedMotion() ? 'auto' : 'smooth'
          });

          if (history.pushState) {
            history.pushState(null, '', targetId);
          }
        });
      });
    },

    initExternalLinks(scope = document) {
      const links = scope.querySelectorAll('a[target="_blank"]');
      if (!links.length) return;

      links.forEach(link => {
        const relValues = new Set((link.getAttribute('rel') || '').split(' ').filter(Boolean));
        relValues.add('noopener');
        relValues.add('noreferrer');
        link.setAttribute('rel', Array.from(relValues).join(' '));
      });
    },

    showElement(element) {
      const delay = parseInt(element.dataset.mbbRevealDelay, 10);
      const cssDelay = parseInt(getComputedStyle(element).getPropertyValue('--mbb-reveal-delay'), 10);
      const finalDelay = Number.isFinite(delay) ? delay : Number.isFinite(cssDelay) ? cssDelay : 0;

      if (this.prefersReducedMotion() || finalDelay <= 0) {
        element.classList.add(this.classes.visible, 'active');
        return;
      }

      window.setTimeout(() => {
        element.classList.add(this.classes.visible, 'active');
      }, finalDelay);
    },

    getHeaderOffset() {
      const header = document.querySelector('[data-mbb-header], .shopify-section-header-sticky, .header-wrapper');
      if (!header) return 20;
      return header.getBoundingClientRect().height + 20;
    },

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    refresh(scope = document) {
      this.initStaggerAnimations(scope);
      this.initRevealAnimations(scope);
      this.initSmoothScroll(scope);
      this.initExternalLinks(scope);
    }
  };

  const run = () => MBB.init();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  document.addEventListener('shopify:section:load', event => {
    MBB.refresh(event.target);
  });

  document.addEventListener('shopify:block:select', event => {
    const element = event.target.closest('.mbb-reveal, [data-mbb-reveal]');
    if (element) MBB.showElement(element);
  });

  window.MyBoardingBox = MBB;
})();